import { SendOutlined } from '@ant-design/icons';
import { Modal, ModalProps } from 'antd';
import { Helmet } from 'react-helmet-async';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { JobPostingDetail } from '../types';
import EmailForm, { EmailFormValues } from './EmailForm';
import Form from './Form';
import { fixUnorderedListInQuill } from './RichEditor/RichEditor';

export interface SendEmailModalProps extends ModalProps {
  jobPostingId?: JobPostingDetail['id'];
  helmetTitle: string;
  loading?: boolean;
  onFinish?: (values: EmailFormValues) => void;
}

const SendEmailModal = ({
  jobPostingId,
  helmetTitle = '',
  loading,
  onFinish,
  ...props
}: SendEmailModalProps) => {
  const [form] = Form.useForm();

  const emailCommunicationTemplatesPages = useInfiniteAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type: 'COMMUNICATION',
        individual: false,
      },
    },
  ]);

  const emailCommunicationTemplates = usePrefetchPages(
    emailCommunicationTemplatesPages,
    (data) => data.results
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  const user = useAPIQuery('/users/me/');

  return (
    <Modal
      okButtonProps={{ htmlType: 'submit', icon: <SendOutlined />, loading }}
      okText="Enviar"
      onOk={() => form.submit()}
      {...props}
    >
      <Helmet>
        <title>{helmetTitle} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<EmailFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          subject: '',
          message: '',
          fromName: config.data?.companyFromName || '',
          replyTo: user.data?.email,
          enableReplyTo: false,
          email_communication_template: null,
          attachments: [],
        }}
        onFinish={(variables) =>
          onFinish?.({ ...variables, message: fixUnorderedListInQuill(variables.message) })
        }
      >
        <EmailForm
          templates={emailCommunicationTemplates.map((emailCommunicationTemplate) => ({
            id: emailCommunicationTemplate.id,
            name: emailCommunicationTemplate.name,
            message: emailCommunicationTemplate.message,
            subject: emailCommunicationTemplate.subject,
            fromName: emailCommunicationTemplate.from_name,
            attachments: emailCommunicationTemplate.attachments,
          }))}
          attachments
          customVariables={[
            { value: '[candidato]', label: 'Nome do Candidato' },
            { value: '[empresa]', label: 'Nome da Empresa' },
            ...(jobPostingId ? [{ value: '[vaga]', label: 'Nome da Vaga' }] : []),
          ]}
          image
          replyTo
          fromName
          richEditorRules={[
            {
              validator(_, value: string) {
                const cleanHtmlRegex = /<[^>]*>/g;

                if (!value.replaceAll(cleanHtmlRegex, '').trim()) {
                  return Promise.reject(new Error('Campo obrigatório.'));
                }

                return Promise.resolve();
              },
            },
            {
              validator(_, value: string) {
                const variablesRegex = /\[vaga\]/;

                if (!jobPostingId && variablesRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      'Não é possível utilizar a variável [vaga] ao enviar email pelo Banco de Talentos'
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        />
      </Form>
    </Modal>
  );
};

export default SendEmailModal;
